/**
 * NBA club colours, for the small identifier squares beside a team code.
 *
 * ═══ WHY THESE ARE HEX AND NOT ROLE TOKENS ═══
 *
 * `.stylelintrc.json` bans raw hex in `*.module.css` so that colour has to come
 * from a named design-system role — and that rule is right, which is exactly why
 * these live here instead. A club's colours are an EXTERNAL BRAND FACT, like its
 * name or its logo. They are not part of KEV's palette, they carry no meaning in
 * the role split (`--cyan` is data, `--violet` is attitude), and forcing them
 * through it would either corrupt the split or produce thirty tokens that mean
 * nothing to the design system.
 *
 * So they are data, applied inline, and the stylelint rule keeps doing its job on
 * everything that IS ours.
 *
 * ═══ WHY TWO COLOURS AND NOT ONE ═══
 *
 * Primaries collide. `#CE1141` is Chicago, Houston AND Toronto; `#C8102E` is both
 * Detroit and the Clippers; `#E03A3E` is Atlanta and Portland. A single-colour
 * square would tell a reader "one of three teams", which is worse than useless on
 * a surface whose whole job is identification at a glance. The second colour
 * separates every one of those pairs.
 *
 * ═══ WHY EVERY SQUARE HAS A RING ═══
 *
 * Several clubs are near-black on our ground — Brooklyn is literally `#000000`,
 * and Denver, Minnesota, New Orleans, Utah and Washington are all deep navies
 * against `--ink` `#12141c`. Without a hairline the square simply disappears and
 * the row looks broken rather than dark. The ring is drawn from a KEV token, not
 * a hex, because THAT part is ours.
 *
 * ═══ THE SQUARE IS NEVER THE ONLY IDENTIFIER ═══
 *
 * WCAG 1.4.1: colour may not be the sole carrier of information. Every place this
 * renders, the three-letter code sits directly beside it — the square is a fast
 * second channel for people who read it that way, and decorative for everyone
 * else. It is `aria-hidden` for the same reason: a screen reader announcing a
 * colour next to "DET" is noise, not access.
 */

export interface TeamColours {
  /** Club primary, as published. */
  primary: string;
  /** Club secondary — carries the disambiguation, see above. */
  secondary: string;
}

/**
 * Keyed by the three-letter code the API sends (`team`, `opponent`,
 * `currentTeam`). Unknown codes are handled by the component, not by a default
 * here — a missing club should look absent, not like a real club rendered grey.
 */
export const TEAM_COLOURS: Readonly<Record<string, TeamColours>> = {
  ATL: { primary: "#E03A3E", secondary: "#C1D32F" },
  BOS: { primary: "#007A33", secondary: "#BA9653" },
  BKN: { primary: "#000000", secondary: "#FFFFFF" },
  CHA: { primary: "#1D1160", secondary: "#00788C" },
  CHI: { primary: "#CE1141", secondary: "#000000" },
  CLE: { primary: "#860038", secondary: "#FDBB30" },
  DAL: { primary: "#00538C", secondary: "#B8C4CA" },
  DEN: { primary: "#0E2240", secondary: "#FEC524" },
  DET: { primary: "#C8102E", secondary: "#1D42BA" },
  GSW: { primary: "#1D428A", secondary: "#FFC72C" },
  HOU: { primary: "#CE1141", secondary: "#C4CED4" },
  IND: { primary: "#002D62", secondary: "#FDBB30" },
  LAC: { primary: "#C8102E", secondary: "#1D428A" },
  LAL: { primary: "#552583", secondary: "#FDB927" },
  MEM: { primary: "#5D76A9", secondary: "#12173F" },
  MIA: { primary: "#98002E", secondary: "#F9A01B" },
  MIL: { primary: "#00471B", secondary: "#EEE1C6" },
  MIN: { primary: "#0C2340", secondary: "#236192" },
  NOP: { primary: "#0C2340", secondary: "#C8102E" },
  NYK: { primary: "#006BB6", secondary: "#F58426" },
  OKC: { primary: "#007AC1", secondary: "#EF3B24" },
  ORL: { primary: "#0077C0", secondary: "#C4CED4" },
  PHI: { primary: "#006BB6", secondary: "#ED174C" },
  PHX: { primary: "#1D1160", secondary: "#E56020" },
  POR: { primary: "#E03A3E", secondary: "#000000" },
  SAC: { primary: "#5A2D81", secondary: "#63727A" },
  SAS: { primary: "#C4CED4", secondary: "#000000" },
  // Silver, not the black that Toronto also publish: Chicago is red-on-black
  // too, and the pair has to separate them or both squares say "one of these
  // two". Silver is in the Raptors own palette, so this stays a brand fact.
  TOR: { primary: "#CE1141", secondary: "#A1A1A4" },
  UTA: { primary: "#002B5C", secondary: "#F9A01B" },
  WAS: { primary: "#002B5C", secondary: "#E31837" },
};

/**
 * NBA clubs by FULL NAME, onto the codes above.
 *
 * Ned, 2026-10-05: "The nba also needs its colour tiles." The note on
 * `TEAM_COLOURS` says the NBA feed sends a code, and the box score and fantasy
 * feeds do. The PUBLIC FIXTURE rows do not: they name both clubs in full
 * ("Boston Celtics", "LA Clippers"), exactly as the NFL rows do. Every NBA row on
 * the fixture list therefore looked up "BOSTON CELTICS" in a table keyed "BOS",
 * found nothing, and drew an empty tile beside clubs that football and the NFL
 * colour in. This is the bridge, and it lives in the shared lookup so no call
 * site has to know which form a given feed sends.
 *
 * Keyed the same way as the football and NFL tables (trimmed, lower case,
 * single-spaced) with full stops dropped, so "L.A. Clippers" lands too. Variants
 * are the spellings the NBA itself and the feeds we read use; the Clippers and
 * Lakers appear both as "LA" and "Los Angeles". Every one of the 30 clubs is
 * pinned by `teamColours.test.ts`.
 *
 * ⚠️ THIS DOES NOT BRING BACK THE LAC TRAP. These are NBA NAMES and only NBA
 * names: "Los Angeles Chargers" is not here, so it can never resolve to the
 * Clippers' colours, and `clubColours` (the NFL and football lookup) never reads
 * this table at all.
 */
const NBA_NAMES: Readonly<Record<string, string>> = {
  "atlanta hawks": "ATL",
  "boston celtics": "BOS",
  "brooklyn nets": "BKN",
  "charlotte hornets": "CHA",
  "chicago bulls": "CHI",
  "cleveland cavaliers": "CLE",
  "dallas mavericks": "DAL",
  "denver nuggets": "DEN",
  "detroit pistons": "DET",
  "golden state warriors": "GSW",
  "houston rockets": "HOU",
  "indiana pacers": "IND",
  "la clippers": "LAC",
  "los angeles clippers": "LAC",
  "la lakers": "LAL",
  "los angeles lakers": "LAL",
  "memphis grizzlies": "MEM",
  "miami heat": "MIA",
  "milwaukee bucks": "MIL",
  "minnesota timberwolves": "MIN",
  "new orleans pelicans": "NOP",
  "new york knicks": "NYK",
  "oklahoma city thunder": "OKC",
  "orlando magic": "ORL",
  "philadelphia 76ers": "PHI",
  "phoenix suns": "PHX",
  "portland trail blazers": "POR",
  "portland trailblazers": "POR",
  "sacramento kings": "SAC",
  "san antonio spurs": "SAS",
  "toronto raptors": "TOR",
  "utah jazz": "UTA",
  "washington wizards": "WAS",
};

/**
 * The NBA code for a club given as a code ("BOS") or a full name ("Boston
 * Celtics"), or `undefined` when it is neither. The one place both forms meet.
 */
export function nbaCode(club: string | null | undefined): string | undefined {
  if (!club) return undefined;
  const trimmed = club.trim();
  const upper = trimmed.toUpperCase();
  if (upper in TEAM_COLOURS) return upper;
  return NBA_NAMES[trimmed.toLowerCase().replace(/\./g, "").replace(/\s+/g, " ")];
}

/** Every NBA full name the lookup accepts — what the test walks. */
export const NBA_FULL_NAMES: readonly string[] = Object.keys(NBA_NAMES);

/**
 * Undefined for anything not a club we have colours for — see the note above.
 * Takes the code or the full name; see `NBA_NAMES` on why both arrive.
 */
export function teamColours(code: string | null | undefined): TeamColours | undefined {
  const resolved = nbaCode(code);
  return resolved ? TEAM_COLOURS[resolved] : undefined;
}

/* ════════════════════════════════════════════════════════════════════════════
   FOOTBALL — Premier League clubs.

   Same three arguments as the NBA block above (external brand fact, two colours
   for disambiguation, never the sole identifier), plus two that only apply here.

   ═══ WHY CLUBS ARE KEYED BY NAME AND NOT A CODE ═══

   The NBA feed sends a stable three-letter code. The football feeds do not send
   any code at all, and worse, the two we hold disagree about the NAME: the squad
   feed says "Newcastle United" and the results feed says "Newcastle". So the
   lookup normalises rather than demanding an exact key — the alternative is a
   club silently rendering with no colour on one surface and a colour on another,
   which reads as a rendering bug rather than a data one.

   ═══ ⚠️ WHERE THESE CAME FROM, AND WHICH ONES DID NOT ═══

   Twelve are transcribed from teamcolorcodes.com, which is the source Ned asked
   for. EIGHT ARE NOT ON THAT SITE — its Premier League index is several seasons
   out of date (it still lists Burnley, Leicester, Norwich, Southampton, Watford
   and Wolves, and has no page at all for Brentford, Fulham, Nottingham Forest,
   Leeds, Sunderland, Ipswich, Hull or Coventry).

   Those eight carry the clubs' widely published primaries and are marked
   `sourced: false`. They are right to the eye and NOT verified against an
   official brand guide, which matters if these ever go near print or anything
   claiming to be official. Guessing quietly would have made twenty clubs look
   equally authoritative when twelve are and eight are approximations.
   ════════════════════════════════════════════════════════════════════════════ */

export interface ClubColours extends TeamColours {
  /** True when the pair is transcribed from teamcolorcodes.com. See above. */
  sourced: boolean;
}

export const CLUB_COLOURS: Readonly<Record<string, ClubColours>> = {
  /* ── from teamcolorcodes.com ─────────────────────────────────────────── */
  "afc bournemouth": { primary: "#DA291C", secondary: "#000000", sourced: true },
  arsenal: { primary: "#EF0107", secondary: "#063672", sourced: true },
  "aston villa": { primary: "#670E36", secondary: "#95BFE5", sourced: true },
  "brighton & hove albion": { primary: "#0057B8", secondary: "#FFCD00", sourced: true },
  chelsea: { primary: "#034694", secondary: "#DBA111", sourced: true },
  "crystal palace": { primary: "#1B458F", secondary: "#C4122E", sourced: true },
  everton: { primary: "#003399", secondary: "#FFFFFF", sourced: true },
  liverpool: { primary: "#C8102E", secondary: "#00B2A9", sourced: true },
  "manchester city": { primary: "#6CABDD", secondary: "#1C2C5B", sourced: true },
  "manchester united": { primary: "#DA291C", secondary: "#FBE122", sourced: true },
  /* Newcastle's own primary is #241F20 — near-black, and the ring the NBA block
     describes is doing real work here. The blue is the secondary so the swatch
     separates them from Bournemouth's black at a glance. */
  "newcastle united": { primary: "#241F20", secondary: "#41B6E6", sourced: true },
  "tottenham hotspur": { primary: "#132257", secondary: "#FFFFFF", sourced: true },

  /* ── NOT on that site: published primaries, unverified ───────────────── */
  brentford: { primary: "#E30613", secondary: "#FBB800", sourced: false },
  "coventry city": { primary: "#78D0F3", secondary: "#001F5B", sourced: false },
  /* White on white is invisible on a light ground, so Fulham lead with the
     black. Both are the club's, and the pair is what identifies them. */
  fulham: { primary: "#000000", secondary: "#CC0000", sourced: false },
  "hull city": { primary: "#F5A12D", secondary: "#000000", sourced: false },
  "ipswich town": { primary: "#0044A9", secondary: "#FFFFFF", sourced: false },
  "leeds united": { primary: "#1D428A", secondary: "#FFCD00", sourced: false },
  "nottingham forest": { primary: "#DD0000", secondary: "#FFFFFF", sourced: false },
  sunderland: { primary: "#EB172B", secondary: "#FFFFFF", sourced: false },

  /* ════════════════════════════════════════════════════════════════════════
     LA LIGA, SERIE A, BUNDESLIGA, LIGUE 1 — the other four leagues the fixture
     list actually carries (`GET /teaser?artifact=fixtures-public`, fetched
     2026-09-07: `epl`, `laliga`, `seriea`, `bundesliga`, `ligue1`, 20 clubs
     each except the last two at 18 — 96 clubs across all five).

     Keyed off that fetch's exact `home`/`away` strings — none of these clubs
     are on teamcolorcodes.com at all (that site is EPL-only), so every entry
     here is `sourced: false`: a club's widely published primary/secondary
     from general knowledge, not a transcription. Same discipline as the eight
     EPL clubs above — right to the eye, not verified against an official
     brand guide.

     None of these hexes are lightened for contrast — see TeamSwatch.module.css's
     own note on why: the ring around the square carries the contrast
     requirement instead, so the club's colour can stay what the club
     actually publishes rather than a display-safe approximation of it.
     ════════════════════════════════════════════════════════════════════════ */

  /* ── Serie A ─────────────────────────────────────────────────────────── */
  "ac milan": { primary: "#FB090B", secondary: "#000000", sourced: false },
  "as roma": { primary: "#8E1F2F", secondary: "#F0BC42", sourced: false },
  atalanta: { primary: "#1E71B8", secondary: "#000000", sourced: false },
  bologna: { primary: "#A6192E", secondary: "#142752", sourced: false },
  cagliari: { primary: "#C8102E", secondary: "#041E42", sourced: false },
  como: { primary: "#003C7D", secondary: "#FFFFFF", sourced: false },
  fiorentina: { primary: "#482E92", secondary: "#FFFFFF", sourced: false },
  frosinone: { primary: "#FFDD00", secondary: "#003399", sourced: false },
  genoa: { primary: "#C41E3A", secondary: "#002F6C", sourced: false },
  inter: { primary: "#010E80", secondary: "#000000", sourced: false },
  juventus: { primary: "#000000", secondary: "#FFFFFF", sourced: false },
  lazio: { primary: "#A5D8F2", secondary: "#FFFFFF", sourced: false },
  lecce: { primary: "#FFE800", secondary: "#C8102E", sourced: false },
  monza: { primary: "#E4022D", secondary: "#FFFFFF", sourced: false },
  napoli: { primary: "#12A0D7", secondary: "#003399", sourced: false },
  parma: { primary: "#FFDE00", secondary: "#002B7F", sourced: false },
  sassuolo: { primary: "#00A650", secondary: "#000000", sourced: false },
  torino: { primary: "#7B1E3A", secondary: "#FFFFFF", sourced: false },
  udinese: { primary: "#000000", secondary: "#FFFFFF", sourced: false },
  venezia: { primary: "#00693E", secondary: "#000000", sourced: false },

  /* ── La Liga ─────────────────────────────────────────────────────────── */
  alaves: { primary: "#0047AB", secondary: "#FFFFFF", sourced: false },
  "athletic club": { primary: "#EE2523", secondary: "#FFFFFF", sourced: false },
  "atletico madrid": { primary: "#CB3524", secondary: "#272E61", sourced: false },
  barcelona: { primary: "#A50044", secondary: "#004D98", sourced: false },
  "celta vigo": { primary: "#8AC3EE", secondary: "#FFFFFF", sourced: false },
  "deportivo la coruna": { primary: "#0056A8", secondary: "#FFFFFF", sourced: false },
  elche: { primary: "#00A650", secondary: "#FFFFFF", sourced: false },
  espanyol: { primary: "#007FC8", secondary: "#FFFFFF", sourced: false },
  getafe: { primary: "#005CA9", secondary: "#FFFFFF", sourced: false },
  levante: { primary: "#002D5B", secondary: "#C8102E", sourced: false },
  malaga: { primary: "#1B458F", secondary: "#FFFFFF", sourced: false },
  osasuna: { primary: "#D2122E", secondary: "#001A4B", sourced: false },
  "racing santander": { primary: "#007A3D", secondary: "#FFFFFF", sourced: false },
  /* Rayo's actual primary is white — invisible on our ground, same problem
     Fulham has above, so the red sash leads instead. Both are the club's. */
  "rayo vallecano": { primary: "#C8102E", secondary: "#FFFFFF", sourced: false },
  "real betis": { primary: "#00954C", secondary: "#FFFFFF", sourced: false },
  /* Real Madrid's own primary is white — same problem, so this leads with the
     gold/purple trim the white kit actually carries rather than the white
     itself. */
  "real madrid": { primary: "#FEBE10", secondary: "#1F1547", sourced: false },
  "real sociedad": { primary: "#0033A0", secondary: "#FFFFFF", sourced: false },
  /* Sevilla's primary is white too — leads with the red instead. */
  sevilla: { primary: "#D2122E", secondary: "#FFFFFF", sourced: false },
  /* Valencia's kit is white — leads with the crest's orange/black instead. */
  valencia: { primary: "#EE8000", secondary: "#000000", sourced: false },
  villarreal: { primary: "#FFE667", secondary: "#005187", sourced: false },

  /* ── Bundesliga ──────────────────────────────────────────────────────── */
  "1. fc köln": { primary: "#ED1C24", secondary: "#FFFFFF", sourced: false },
  "1899 hoffenheim": { primary: "#1C63B7", secondary: "#FFFFFF", sourced: false },
  "bayer leverkusen": { primary: "#E32221", secondary: "#000000", sourced: false },
  "bayern münchen": { primary: "#DC052D", secondary: "#0066B2", sourced: false },
  "borussia dortmund": { primary: "#FDE100", secondary: "#000000", sourced: false },
  "borussia mönchengladbach": { primary: "#000000", secondary: "#007A3D", sourced: false },
  "eintracht frankfurt": { primary: "#000000", secondary: "#E1000F", sourced: false },
  "fc augsburg": { primary: "#BA3733", secondary: "#00893A", sourced: false },
  "fc schalke 04": { primary: "#004C9B", secondary: "#FFFFFF", sourced: false },
  "fsv mainz 05": { primary: "#C4122E", secondary: "#FFFFFF", sourced: false },
  "hamburger sv": { primary: "#0C1C8C", secondary: "#FFFFFF", sourced: false },
  "rb leipzig": { primary: "#DD0741", secondary: "#FFFFFF", sourced: false },
  "sc freiburg": { primary: "#E4022D", secondary: "#000000", sourced: false },
  "sc paderborn 07": { primary: "#003399", secondary: "#000000", sourced: false },
  "sv elversberg": { primary: "#0066B3", secondary: "#000000", sourced: false },
  "union berlin": { primary: "#EB1923", secondary: "#FFCC00", sourced: false },
  "vfb stuttgart": { primary: "#E32219", secondary: "#FFFFFF", sourced: false },
  "werder bremen": { primary: "#1D9053", secondary: "#FFFFFF", sourced: false },

  /* ── Ligue 1 ─────────────────────────────────────────────────────────── */
  angers: { primary: "#000000", secondary: "#FFFFFF", sourced: false },
  auxerre: { primary: "#0F5FA6", secondary: "#FFFFFF", sourced: false },
  "estac troyes": { primary: "#1B2A4A", secondary: "#F58220", sourced: false },
  "le havre": { primary: "#4AA8DE", secondary: "#FFFFFF", sourced: false },
  "le mans": { primary: "#003DA5", secondary: "#FFFFFF", sourced: false },
  lens: { primary: "#E2001A", secondary: "#FFD200", sourced: false },
  lille: { primary: "#C8102E", secondary: "#002F6C", sourced: false },
  lorient: { primary: "#F7941D", secondary: "#000000", sourced: false },
  lyon: { primary: "#0055A4", secondary: "#C8102E", sourced: false },
  marseille: { primary: "#2FAEE0", secondary: "#FFFFFF", sourced: false },
  monaco: { primary: "#ED1C24", secondary: "#FFFFFF", sourced: false },
  nice: { primary: "#D2122E", secondary: "#000000", sourced: false },
  "paris fc": { primary: "#003087", secondary: "#ED1C24", sourced: false },
  "paris saint germain": { primary: "#004170", secondary: "#DA291C", sourced: false },
  rennes: { primary: "#E2001A", secondary: "#000000", sourced: false },
  "stade brestois 29": { primary: "#E2001A", secondary: "#FFFFFF", sourced: false },
  strasbourg: { primary: "#1E5AA8", secondary: "#FFFFFF", sourced: false },
  toulouse: { primary: "#6B2C91", secondary: "#FFFFFF", sourced: false },
};

/* ════════════════════════════════════════════════════════════════════════════
   NFL — all 32 clubs.

   ═══ KEYED BY FULL NAME, LIKE FOOTBALL AND UNLIKE THE NBA ═══

   The fixture feed names NFL clubs in full — "Seattle Seahawks", "San Francisco
   49ers", "Los Angeles Rams" (`GET /teaser?artifact=fixtures-public`, fetched
   2026-09-07: 16 NFL fixtures, which is every one of the 32 clubs exactly once).
   So they go through `clubColours`, the same normalising lookup football uses,
   and the same `ClubSwatch` renders them.

   ═══ ⚠️ WHY NOT THE THREE-LETTER CODES ═══

   Because `TEAM_COLOURS` above is keyed on codes and the two leagues share
   almost all of them: ATL, CHI, CLE, DAL, DEN, DET, HOU, IND, MIA, MIN, PHI and
   WAS are a club in each sport. `LAC` is the genuinely dangerous one — the
   Clippers in that table, the Chargers here — and a Chargers swatch rendered in
   Clippers red-and-blue would look entirely deliberate, because both clubs
   really do play in colours of roughly that family. Washington is the same trap
   with different colours: the Wizards' navy/red would pass for the Commanders'
   burgundy/gold at swatch size.

   Full names collide with nothing. That is asserted, not assumed —
   TeamSwatch.test.tsx pins this table's keys disjoint from `CLUB_COLOURS`'s, so
   no sport can quietly win a name off another and the lookup order below never
   decides anything.

   ═══ WHERE THESE CAME FROM ═══

   All 32 are transcribed from teamcolorcodes.com, the source Ned gave. Unlike
   its several-seasons-stale Premier League index, its NFL page carries every
   current club, so every entry here is `sourced: true` and none is a guess.

   Where the site publishes more than two colours the pair kept is the one that
   IDENTIFIES the club, which is the same argument the NBA block makes and the
   NFL needs badly: Dallas and the Rams both lead #003594, so Dallas takes its
   silver and the Rams their gold; Cincinnati and Denver both lead #FB4F14, and
   black against navy separates them; #002244 is New England's primary, Denver's
   secondary AND Seattle's primary, so in all three the other half of the square
   is what says which club it is.

   ═══ NEAR-BLACK PRIMARIES ARE LEFT ALONE ═══

   Las Vegas is literally #000000, and Chicago, Houston, Jacksonville, Cleveland
   and Tennessee are near-black against `--ink`. Nothing here is lightened for
   contrast, for exactly the reason TeamSwatch.module.css sets out: the `--muted`
   ring carries the visibility requirement (6.04:1 against `--ink`), so the fill
   stays the colour the club actually publishes rather than a display-safe
   approximation of it. Brooklyn (#000000) and Newcastle (#241F20) already sit in
   the two tables above under that same rule — this adds no new one.
   ════════════════════════════════════════════════════════════════════════════ */

export const NFL_COLOURS: Readonly<Record<string, ClubColours>> = {
  /* ── AFC East ────────────────────────────────────────────────────────── */
  "buffalo bills": { primary: "#00338D", secondary: "#C60C30", sourced: true },
  "miami dolphins": { primary: "#008E97", secondary: "#FC4C02", sourced: true },
  "new england patriots": { primary: "#002244", secondary: "#C60C30", sourced: true },
  "new york jets": { primary: "#125740", secondary: "#000000", sourced: true },

  /* ── AFC North ───────────────────────────────────────────────────────── */
  "baltimore ravens": { primary: "#241773", secondary: "#9E7C0C", sourced: true },
  "cincinnati bengals": { primary: "#FB4F14", secondary: "#000000", sourced: true },
  "cleveland browns": { primary: "#311D00", secondary: "#FF3C00", sourced: true },
  /* The site lists Steelers Gold first and the black second; kept in that order,
     and the pair is unmistakable either way round. */
  "pittsburgh steelers": { primary: "#FFB612", secondary: "#101820", sourced: true },

  /* ── AFC South ───────────────────────────────────────────────────────── */
  "houston texans": { primary: "#03202F", secondary: "#A71930", sourced: true },
  "indianapolis colts": { primary: "#002C5F", secondary: "#A2AAAD", sourced: true },
  "jacksonville jaguars": { primary: "#101820", secondary: "#D7A22A", sourced: true },
  "tennessee titans": { primary: "#0C2340", secondary: "#4B92DB", sourced: true },

  /* ── AFC West ────────────────────────────────────────────────────────── */
  "denver broncos": { primary: "#FB4F14", secondary: "#002244", sourced: true },
  "kansas city chiefs": { primary: "#E31837", secondary: "#FFB81C", sourced: true },
  "las vegas raiders": { primary: "#000000", secondary: "#A5ACAF", sourced: true },
  "los angeles chargers": { primary: "#0080C6", secondary: "#FFC20E", sourced: true },

  /* ── NFC East ────────────────────────────────────────────────────────── */
  /* Silver, not the navy the site also lists: the Rams lead the identical
     #003594, so the second colour is the whole disambiguation here. */
  "dallas cowboys": { primary: "#003594", secondary: "#869397", sourced: true },
  "new york giants": { primary: "#0B2265", secondary: "#A71930", sourced: true },
  "philadelphia eagles": { primary: "#004C54", secondary: "#A5ACAF", sourced: true },
  "washington commanders": { primary: "#5A1414", secondary: "#FFB612", sourced: true },

  /* ── NFC North ───────────────────────────────────────────────────────── */
  "chicago bears": { primary: "#0B162A", secondary: "#C83803", sourced: true },
  "detroit lions": { primary: "#0076B6", secondary: "#B0B7BC", sourced: true },
  "green bay packers": { primary: "#203731", secondary: "#FFB612", sourced: true },
  "minnesota vikings": { primary: "#4F2683", secondary: "#FFC62F", sourced: true },

  /* ── NFC South ───────────────────────────────────────────────────────── */
  "atlanta falcons": { primary: "#A71930", secondary: "#000000", sourced: true },
  "carolina panthers": { primary: "#0085CA", secondary: "#101820", sourced: true },
  "new orleans saints": { primary: "#D3BC8D", secondary: "#101820", sourced: true },
  /* Pewter, not black. The site's NFL index lists "#FF7900 (Black)" for Tampa,
     which is a typo on that page — its own Buccaneers page gives black as
     #0A0A08 and pewter as #34302B. Pewter is also the colour that separates
     them from every other red club, so it is the one kept. */
  "tampa bay buccaneers": { primary: "#D50A0A", secondary: "#34302B", sourced: true },

  /* ── NFC West ────────────────────────────────────────────────────────── */
  "arizona cardinals": { primary: "#97233F", secondary: "#000000", sourced: true },
  "los angeles rams": { primary: "#003594", secondary: "#FFA300", sourced: true },
  "san francisco 49ers": { primary: "#AA0000", secondary: "#B3995D", sourced: true },
  "seattle seahawks": { primary: "#002244", secondary: "#69BE28", sourced: true },
};

/** Short forms the RESULTS feed uses where the squad feed spells them out. */
const CLUB_ALIASES: Readonly<Record<string, string>> = {
  bournemouth: "afc bournemouth",
  brighton: "brighton & hove albion",
  "brighton and hove albion": "brighton & hove albion",
  coventry: "coventry city",
  hull: "hull city",
  ipswich: "ipswich town",
  leeds: "leeds united",
  newcastle: "newcastle united",
  "nottm forest": "nottingham forest",
  spurs: "tottenham hotspur",
  tottenham: "tottenham hotspur",
  "man city": "manchester city",
  "man utd": "manchester united",
  "man united": "manchester united",
};

/**
 * Colours for a football or NFL club under any spelling its feed uses.
 *
 * Both tables are searched because both are keyed the same way — on the club's
 * full name, normalised — and their key sets are proven disjoint in
 * TeamSwatch.test.tsx, so the order below is a reading order and not a
 * precedence rule. If a name ever DID land in both, that test fails rather than
 * one sport silently taking the other's colours; see NFL_COLOURS' note on why
 * codes would have made that failure mode routine.
 *
 * Aliases are football-only, and resolved last: the NFL feed sends full names
 * and only full names, so there is nothing to shorten.
 *
 * Undefined for anything unknown, deliberately — the NBA note applies verbatim:
 * a missing club must look absent rather than like a real club rendered grey.
 */
export function clubColours(name: string | null | undefined): ClubColours | undefined {
  if (!name) return undefined;
  const key = name.trim().toLowerCase().replace(/\s+/g, " ");
  return CLUB_COLOURS[key] ?? NFL_COLOURS[key] ?? CLUB_COLOURS[CLUB_ALIASES[key] ?? ""];
}
